import React, { useEffect, useState, useRef, createElement, Component } from 'react';
import { Share2, AlertTriangle, Shield, MessageSquare, TrendingDown, RefreshCw, Trash2, ArrowLeft, Check, X, ChevronDown, ChevronUp, Info, Sparkles, Activity } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { WebsiteHeader } from '../components/Dashboard/WebsiteHeader';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useScan, deleteScan } from '../hooks/useScans';
import { ShareableResultCard } from '../components/Analysis/ShareableResultCard';
import { SocialShareModal } from '../components/Analysis/SocialShareModal';
import { LoadingAnimation } from '../components/Analysis/LoadingAnimation';
import { NotificationBanner } from '../components/Notifications/NotificationBanner';
import { FindingCard } from '../components/Analysis/FindingCard';
import { TeaserResultCard } from '../components/Analysis/TeaserResultCard';
import { trackShare, trackCriticalEvent } from '../utils/mixpanelTracking';
import { useToast } from '../contexts/ToastContext';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { createPayment } from '../services/paymentService';
import { generateCaseFilePDF } from '../utils/pdfGenerator';
import { useGoogleReview } from '../contexts/GoogleReviewContext';
import { FirstScanReviewModal } from '../components/ToxicDetector/FirstScanReviewModal';
// --- Types ---
type TabType = 'overview' | 'patterns' | 'timeline';
type AspectRatio = 'square' | 'story';
// Social Media Caption Templates
const SOCIAL_CAPTIONS = {
  instagram_story: `I didn't expect this result.

Some communication patterns aren't obvious until you see them clearly.

🔍 Analyzed with ToxicDetector.ai`,
  instagram_feed: `This conversation showed concerning communication patterns I hadn't noticed before.

Tools like this don't judge — they help you see patterns more clearly.

🧠 Made with ToxicDetector.ai`,
  tiktok: `Some red flags don't look like red flags at first.

This analysis broke it down clearly.

#Communication #Relationships #ToxicDetector`,
  twitter: `This conversation showed concerning communication patterns I didn't catch on my own.

Insight matters.

Analyzed with ToxicDetector.ai`,
  youtube: `Some communication issues are subtle — but patterns tell a story.

This chat analysis highlighted behaviors worth paying attention to.

Made with ToxicDetector.ai`,
  facebook: `This conversation showed concerning communication patterns I hadn't noticed before.

Tools like this don't judge — they help you see patterns more clearly.

🧠 Made with ToxicDetector.ai`
};
export function AnalysisPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const scanId = searchParams.get('id');
  const {
    scan,
    loading,
    error,
    refetch,
    statusChange,
    clearStatusChange
  } = useScan(scanId || undefined);
  const {
    user
  } = useAuth();
  const {
    showToast
  } = useToast();
  const {
    scanCount
  } = useGoogleReview();
  // State
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareAspectRatio, setShareAspectRatio] = useState<AspectRatio>('story');
  const [shareCardTheme, setShareCardTheme] = useState<'dark' | 'light'>('dark');
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [showFirstScanReview, setShowFirstScanReview] = useState(false);
  // Refs for image capture
  const squareCaptureRef = useRef<HTMLDivElement>(null);
  const storyCaptureRef = useRef<HTMLDivElement>(null);
  // --- Effects ---
  // Handle scan status changes with toast notifications
  useEffect(() => {
    if (statusChange) {
      const {
        currentStatus,
        previousStatus,
        scan: changedScan
      } = statusChange;
      if (currentStatus === 'completed') {
        // Dispatch event for Google Review prompt
        window.dispatchEvent(new CustomEvent('scanCompleted'));
        // Show success toast with score info
        const riskText = changedScan.risk_level === 'high' ? '⚠️ High risk patterns detected' : changedScan.risk_level === 'medium' ? '⚡ Some patterns to review' : '✅ Communication looks healthy';
        showToast(`Analysis Complete! Score: ${changedScan.score}/100. ${riskText}`, 'success', 5000);
      } else if (currentStatus === 'failed') {
        showToast('Analysis failed. Please try again or contact support if the issue persists.', 'error', 6000);
      }
      // Clear the status change after handling
      clearStatusChange();
    }
  }, [statusChange, clearStatusChange, showToast]);
  useEffect(() => {
    if (scan && scan.status === 'completed' && !showNotification) {
      setShowNotification(true);
    }
  }, [scan]);
  // First Scan Review Prompt — only for high-scoring first scans, respects dismissal
  useEffect(() => {
    if (scan?.status === 'completed') {
      const hasShown = localStorage.getItem('first_scan_review_shown');
      const hasDismissed = localStorage.getItem('first_scan_review_dismissed');
      const scanScore = scan.score || 0;
      if (scanCount === 1 && scanScore >= 70 && !hasShown && !hasDismissed) {
        const timer = setTimeout(() => {
          setShowFirstScanReview(true);
          localStorage.setItem('first_scan_review_shown', 'true');
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [scan?.status, scan?.score, scanCount]);
  // Track analysis view on mount
  useEffect(() => {
    if (scan && scan.status === 'completed' && user?.id) {
      const timeSinceCompletion = scan.updated_at ? Math.floor((Date.now() - new Date(scan.updated_at).getTime()) / 1000) : 0;
      trackCriticalEvent('Analysis_Viewed', {
        scan_id: scan.id,
        toxicity_score: scan.score || 0,
        risk_level: scan.risk_level || 'unknown',
        patterns_count: Array.isArray(scan.patterns) ? scan.patterns.length : 0,
        is_unlocked: scan.is_unlocked,
        time_since_completion: timeSinceCompletion
      });
    }
  }, [scan?.id, scan?.status, user?.id]);
  // --- Handlers ---
  const handleBack = () => navigate('/dashboard');
  const handleTryAgain = () => navigate('/upload');
  const handleUnlock = async () => {
    if (!scanId) return;
    try {
      setUnlockLoading(true);
      // Track unlock attempt
      await trackCriticalEvent('Unlock_Attempt', {
        scan_id: scanId,
        source: 'locked_analysis_page',
        score: scan?.score || 0,
        risk_level: scan?.risk_level
      });
      // Create payment session with scan_id for proper unlock after payment
      const {
        authorization_url
      } = await createPayment('single', {
        scanId
      });
      // Redirect to payment
      window.location.href = authorization_url;
    } catch (error) {
      console.error('Unlock error:', error);
      showToast('Payment initialization failed. Please try again.', 'error');
      setUnlockLoading(false);
    }
  };
  const handleDelete = async () => {
    if (!scanId) return;
    try {
      setDeleting(true);
      // Track deletion
      const daysSinceCreation = scan?.created_at ? Math.floor((Date.now() - new Date(scan.created_at).getTime()) / (1000 * 60 * 60 * 24)) : 0;
      await trackCriticalEvent('Scan_Deleted', {
        scan_id: scanId,
        toxicity_score: scan?.score || 0,
        days_since_creation: daysSinceCreation,
        was_completed: scan?.status === 'completed'
      });
      await deleteScan(scanId);
      showToast('Scan deleted successfully', 'success');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error deleting scan:', error);
      showToast('Failed to delete scan. Please try again.', 'error');
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };
  const handleRetry = async () => {
    if (!scanId) return;
    try {
      setRetrying(true);
      // Track retry
      await trackCriticalEvent('Scan_Retry_Initiated', {
        scan_id: scanId,
        original_failure_reason: 'user_initiated_retry'
      });
      const {
        error
      } = await supabase.from('scans').update({
        status: 'pending',
        analysis: null
      }).eq('id', scanId);
      if (error) throw error;
      showToast('Retry initiated! AI analysis is processing again...', 'success');
      refetch();
    } catch (error) {
      console.error('Error retrying analysis:', error);
      showToast('Failed to retry analysis. Please try again.', 'error');
    } finally {
      setRetrying(false);
    }
  };
  const handleDownloadImage = async () => {
    if (!scanId) return;
    setIsGeneratingImage(true);
    try {
      const element = shareAspectRatio === 'square' ? squareCaptureRef.current : storyCaptureRef.current;
      if (!element) throw new Error('Capture element not found');
      const {
        toPng
      } = await import('html-to-image');
      const dataUrl = await toPng(element, {
        quality: 1,
        pixelRatio: 2
      });
      const link = document.createElement('a');
      link.download = `toxic-report-${scanId}-${shareAspectRatio}.png`;
      link.href = dataUrl;
      link.click();
      showToast('Image downloaded successfully!', 'success');
      // Track download
      const userPlan = user?.user_metadata?.subscriptionPlan || 'free';
      await trackShare('Image_Download', scan?.score || 0, userPlan);
    } catch (error) {
      console.error('Error downloading image:', error);
      showToast('Failed to generate image. Please try again.', 'error');
    } finally {
      setIsGeneratingImage(false);
    }
  };
  const handleDownloadPDF = async () => {
    if (!scanId || !scan) return;
    setIsGeneratingPDF(true);
    try {
      const analysisData = scan.analysis as {
        summary?: string;
        findings?: Array<{
          pattern: string;
          severity: string;
          example: string;
        }>;
        recommendations?: string[];
        messages?: any[];
        [key: string]: any;
      } | null;
      // Resolve risk level from multiple sources (matching UI display logic):
      // 1. scan.risk_level from DB
      // 2. analysis.risk_level (authoritative from backend)
      // 3. Derive from score as final fallback
      const resolvedScore = scan.score || 0;
      const resolvedRiskLevel = scan.risk_level || analysisData?.risk_level || (resolvedScore >= 70 ? 'high' : resolvedScore >= 40 ? 'medium' : 'low');
      // Pass ALL data sources so the PDF generator can resolve the best one:
      // - findings from analysis (if Edge Function returned structured findings)
      // - analysis object (for messages[], summary, recommendations)
      // - patterns from scan (fallback)
      await generateCaseFilePDF({
        scanId: scan.id,
        score: resolvedScore,
        riskLevel: resolvedRiskLevel,
        messageCount: scan.message_count || 0,
        summary: analysisData?.summary,
        findings: analysisData?.findings || [],
        recommendations: analysisData?.recommendations || getRecommendations(resolvedRiskLevel),
        createdAt: scan.created_at,
        patterns: scan.patterns || [],
        analysis: analysisData
      });
      showToast('PDF Case File downloaded successfully!', 'success');
      // Track download
      const userPlan = user?.user_metadata?.subscriptionPlan || 'free';
      await trackShare('PDF_Download', scan.score || 0, userPlan);
    } catch (error) {
      console.error('Error generating PDF:', error);
      showToast('Failed to generate PDF. Please try again.', 'error');
    } finally {
      setIsGeneratingPDF(false);
    }
  };
  const handleSocialShare = async (platform: 'instagram' | 'facebook' | 'tiktok' | 'twitter' | 'youtube' | 'whatsapp') => {
    const shareUrl = `${window.location.origin}/results?id=${scanId}&ref=share&score=${scan?.score || 0}`;
    const SHARE_IMAGE_URL = 'https://cdn.magicpatterns.com/uploads/dTFGJHFfyVyrJCztpQttwz/file_00000000e69c7243bed09cbc104eb039.png';
    let caption = '';
    switch (platform) {
      case 'instagram':
        caption = SOCIAL_CAPTIONS.instagram_feed;
        break;
      case 'tiktok':
        caption = SOCIAL_CAPTIONS.tiktok;
        break;
      case 'twitter':
        caption = SOCIAL_CAPTIONS.twitter;
        break;
      case 'youtube':
        caption = SOCIAL_CAPTIONS.youtube;
        break;
      case 'facebook':
        caption = SOCIAL_CAPTIONS.facebook;
        break;
      case 'whatsapp':
        caption = SOCIAL_CAPTIONS.twitter;
        break;
    }
    try {
      // Try native share with image if supported
      if (navigator.share) {
        try {
          const response = await fetch(SHARE_IMAGE_URL);
          const blob = await response.blob();
          const file = new File([blob], 'toxic-detector-result.png', {
            type: blob.type
          });
          if (navigator.canShare && navigator.canShare({
            files: [file]
          })) {
            await navigator.share({
              title: 'ToxicDetector Analysis',
              text: caption,
              url: shareUrl,
              files: [file]
            });
            // Track share
            const userPlan = user?.user_metadata?.subscriptionPlan || 'free';
            const platformName = platform.charAt(0).toUpperCase() + platform.slice(1);
            await trackShare(platformName, scan?.score || 0, userPlan);
            return;
          }
        } catch (e) {
          console.log('Native file share failed, falling back to text share', e);
        }
      }
      if (['instagram', 'tiktok', 'youtube'].includes(platform)) {
        const fullText = `${caption}\n\n${shareUrl}`;
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(fullText);
          let platformName = platform.charAt(0).toUpperCase() + platform.slice(1);
          showToast(`✓ Caption and link copied! Open ${platformName} and paste it.`, 'success');
        } else {
          const textArea = document.createElement('textarea');
          textArea.value = fullText;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          let platformName = platform.charAt(0).toUpperCase() + platform.slice(1);
          showToast(`✓ Caption and link copied! Open ${platformName} and paste it.`, 'success');
        }
      } else if (platform === 'whatsapp') {
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(caption + ' ' + shareUrl)}`;
        window.open(whatsappUrl, '_blank', 'width=600,height=400');
      } else if (platform === 'facebook') {
        const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(caption)}`;
        window.open(fbUrl, '_blank', 'width=600,height=400');
      } else if (platform === 'twitter') {
        const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}&url=${encodeURIComponent(shareUrl)}`;
        window.open(twitterUrl, '_blank', 'width=600,height=400');
      }
      // Track share
      const userPlan = user?.user_metadata?.subscriptionPlan || 'free';
      const platformName = platform.charAt(0).toUpperCase() + platform.slice(1);
      await trackShare(platformName, scan?.score || 0, userPlan);
    } catch (err) {
      console.error('Share error:', err);
      showToast('Unable to copy link. Please try again.', 'error');
    }
  };
  // --- Render Helpers ---
  if (!scanId) return <ErrorState message="No scan ID provided" onBack={handleBack} data-id="element-3630" />;
  if (loading) return <LoadingState data-id="element-3631" />;
  if (error || !scan) return <ErrorState message={error || 'Scan not found'} onRetry={refetch} onBack={handleBack} data-id="element-3632" />;
  if (scan.status === 'processing' || scan.status === 'pending') return <ProcessingState onBack={handleBack} data-id="element-3633" />;
  if (scan.status === 'failed') return <FailedState onRetry={handleRetry} onBack={handleBack} retrying={retrying} data-id="element-3634" />;
  // LOCKED STATE - Use falsy check instead of strict equality
  // This handles null, undefined, and false values from the database
  if (scan.status === 'completed' && !scan.is_unlocked) {
    const score = scan.score || 0;
    // FIX: Use backend risk_level directly, but also derive from score as fallback
    const riskLevel = scan.risk_level || (score >= 70 ? 'high' : score >= 40 ? 'medium' : 'low');
    // DEBUG: Log scan data to verify backend values
    console.log('[AnalysisPage] Locked state:', {
      score: scan.score,
      risk: scan.risk_level,
      derivedRisk: riskLevel,
      patterns: scan.patterns
    });
    // FIX: Properly check for patterns array
    const hasPatterns = Array.isArray(scan.patterns) && scan.patterns.length > 0;
    const patterns = hasPatterns ? scan.patterns : [];
    return <div className="flex flex-col w-full min-h-screen bg-[#050505] font-sans text-white relative overflow-hidden" data-id="element-3635">
        {/* Grid Background */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none fixed" data-id="element-3636" />

        <WebsiteHeader data-id="element-3637" />

        <main className="flex-1 w-full max-w-4xl mx-auto px-4 sm:px-6 py-8 relative z-10" data-id="element-3638">
          <button onClick={handleBack} className="flex items-center gap-2 text-gray-500 hover:text-white mb-6 transition-colors group" data-id="element-3639">
            <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" data-id="element-3640" />
            <span className="font-mono text-sm uppercase tracking-wider" data-id="element-3641">
              Back to Dashboard
            </span>
          </button>

          {/* Use the new TeaserResultCard component */}
          <TeaserResultCard scanId={scan.id} messageCount={scan.message_count || 0} scanTitle={scan.title} score={score} riskLevel={riskLevel} patterns={patterns} onUnlockSuccess={() => refetch()} data-id="element-3642" />
        </main>
      </div>;
  }
  // Data prep for UNLOCKED state
  const score = scan.score || 0;
  // FIX: Use backend risk_level directly, but derive from score as fallback
  // This ensures correct display even if DB risk_level is null
  const riskLevel = scan.risk_level || (score >= 70 ? 'high' : score >= 40 ? 'medium' : 'low');
  // DEBUG: Log scan data to verify backend values
  console.log('[AnalysisPage] Unlocked state:', {
    score: scan.score,
    risk: scan.risk_level,
    derivedRisk: riskLevel,
    patterns: scan.patterns,
    patternsType: typeof scan.patterns,
    patternsIsArray: Array.isArray(scan.patterns),
    analysis: scan.analysis
  });
  // FIX: Properly check for patterns array - handle both string[] and Pattern[]
  const hasPatterns = Array.isArray(scan.patterns) && scan.patterns.length > 0;
  const patterns = hasPatterns ? scan.patterns : [];
  // Extract analysis data for summary and findings
  const analysis = scan.analysis as {
    summary?: string;
    findings?: Array<{
      pattern: string;
      severity: string;
      example: string;
    }>;
    recommendations?: string[];
  } | null;
  // Get the AI-generated summary message from analysis
  const analysisSummary = analysis?.summary || null;
  const findings = analysis?.findings || [];
  return <div className="flex flex-col w-full min-h-screen bg-slate-50 font-sans text-slate-900" data-id="element-3643">
      <WebsiteHeader data-id="element-3644" />

      {showNotification && <NotificationBanner message="Your analysis is ready to view" onClose={() => setShowNotification(false)} data-id="element-3645" />}

      <main className="flex-1 w-full max-w-4xl mx-auto px-4 sm:px-6 py-8" data-id="element-3646">
        {/* Navigation */}
        <nav className="flex items-center justify-between mb-8" data-id="element-3647">
          <button onClick={handleBack} className="group flex items-center gap-2 text-slate-500 hover:text-slate-800 transition-colors" data-id="element-3648">
            <div className="p-2 rounded-full bg-white border border-slate-200 group-hover:border-slate-300 shadow-sm transition-all" data-id="element-3649">
              <ArrowLeft size={18} data-id="element-3650" />
            </div>
            <span className="font-medium" data-id="element-3651">Back to Dashboard</span>
          </button>

          <div className="flex items-center gap-2" data-id="element-3652">
            <button onClick={() => setShowDeleteConfirm(true)} className="p-2.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all" title="Delete Scan" data-id="element-3653">
              <Trash2 size={20} data-id="element-3654" />
            </button>
          </div>
        </nav>

        {/* Hero Section: Risk Score */}
        <section className="mb-10" data-id="element-3655">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden relative" data-id="element-3656">
            <div className={`absolute top-0 left-0 w-full h-1.5 ${getRiskColorBar(riskLevel)}`} data-id="element-3657" />

            <div className="p-8 md:p-10 flex flex-col md:flex-row items-center gap-10" data-id="element-3658">
              {/* Score Circle */}
              <div className="relative flex-shrink-0" data-id="element-3659">
                <ScoreRing score={score} riskLevel={riskLevel} data-id="element-3660" />
              </div>

              {/* Text Content */}
              <div className="flex-1 text-center md:text-left" data-id="element-3661">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-bold uppercase tracking-wider mb-4 border border-slate-200" data-id="element-3662">
                  <Activity size={12} data-id="element-3663" />
                  {scan.message_count} Messages Analyzed
                </div>

                <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-3 leading-tight tracking-tight" data-id="element-3664">
                  {getRiskHeadline(riskLevel)}
                </h1>

                {/* NEW: Show AI-generated summary message if available */}
                {analysisSummary ? <p className="text-slate-600 text-lg leading-relaxed mb-6 max-w-xl" data-id="element-3665">
                    {analysisSummary}
                  </p> : <p className="text-slate-600 text-lg leading-relaxed mb-6 max-w-xl" data-id="element-3666">
                    {getRiskDescription(riskLevel, patterns.length)}
                  </p>}

                {/* NEW: Show detected patterns summary in hero */}
                {patterns.length > 0 && <div className="flex flex-wrap gap-2 mb-6" data-id="element-3667">
                    {patterns.slice(0, 4).map((pattern, idx) => {
                  const patternType = typeof pattern === 'string' ? pattern : pattern.type || 'Unknown';
                  const patternSeverity = typeof pattern === 'string' ? 'MEDIUM' : pattern.severity || 'MEDIUM';
                  return <span key={idx} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide ${getPatternBadgeColor(patternSeverity)}`} data-id="element-3668">
                          <AlertTriangle size={12} data-id="element-3669" />
                          {patternType}
                        </span>;
                })}
                    {patterns.length > 4 && <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium text-slate-500 bg-slate-100" data-id="element-3670">
                        +{patterns.length - 4} more
                      </span>}
                  </div>}

                <div className="flex flex-wrap items-center justify-center md:justify-start gap-3" data-id="element-3671">
                  <button onClick={() => setShowShareModal(true)} className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5" data-id="element-3672">
                    <Share2 size={18} data-id="element-3673" />
                    Share Result
                  </button>
                  <button onClick={handleTryAgain} className="flex items-center gap-2 px-6 py-3 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl font-semibold transition-all" data-id="element-3674">
                    <RefreshCw size={18} data-id="element-3675" />
                    Try Again
                  </button>
                </div>

                <p className="mt-6 text-xs text-slate-400 flex items-center justify-center md:justify-start gap-1.5 font-mono" data-id="element-3676">
                  <Info size={12} data-id="element-3677" />
                  Based on communication patterns, not intent.
                </p>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Comparison CTA Section */}
        <section className="mb-10" data-id="element-3678">
          <div className="bg-gradient-to-r from-indigo-900 to-slate-900 rounded-3xl p-8 shadow-lg text-center relative overflow-hidden" data-id="element-3679">
            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay pointer-events-none" data-id="element-3680"></div>
            <div className="relative z-10" data-id="element-3681">
              <h3 className="text-2xl font-bold text-white mb-3" data-id="element-3682">
                Is this a pattern?
              </h3>
              <p className="text-indigo-200 mb-6 max-w-lg mx-auto" data-id="element-3683">
                Compare these results with another conversation to see if you're
                dealing with a specific communication style or just a bad day.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-3684">
                <button onClick={() => navigate('/upload')} className="w-full sm:w-auto px-6 py-3 bg-white text-indigo-900 rounded-xl font-bold hover:bg-indigo-50 transition-colors flex items-center justify-center gap-2" data-id="element-3685">
                  <Activity size={18} data-id="element-3686" />
                  Compare with my Ex
                </button>
                <button onClick={() => navigate('/upload')} className="w-full sm:w-auto px-6 py-3 bg-indigo-800 text-white border border-indigo-700 rounded-xl font-bold hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2" data-id="element-3687">
                  <MessageSquare size={18} data-id="element-3688" />
                  Compare with my Bestie
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Tabs */}
        <div className="mb-8" data-id="element-3689">
          <div className="flex p-1 bg-slate-100 rounded-xl gap-1 border border-slate-200" data-id="element-3690">
            <TabButton active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} data-id="element-3691">
              Overview
            </TabButton>
            <TabButton active={activeTab === 'patterns'} onClick={() => setActiveTab('patterns')} data-id="element-3692">
              Detailed Patterns
            </TabButton>
            <TabButton active={activeTab === 'timeline'} onClick={() => setActiveTab('timeline')} data-id="element-3693">
              Timeline
            </TabButton>
          </div>
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait" data-id="element-3694">
          <motion.div key={activeTab} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} data-id="element-3695">
            {activeTab === 'overview' && <div className="space-y-6" data-id="element-3696">
                {/* Patterns Summary */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3697">
                  {patterns.slice(0, 4).map((pattern, idx) => <PatternCard key={idx} pattern={pattern} index={idx} compact data-id="element-3698" />)}
                </div>

                {patterns.length === 0 && <EmptyState data-id="element-3699" />}

                {/* Recommendations */}
                <div className="bg-gradient-to-br from-indigo-50 to-white rounded-2xl p-8 border border-indigo-100 shadow-sm" data-id="element-3700">
                  <h3 className="text-lg font-bold text-indigo-900 mb-6 flex items-center gap-2" data-id="element-3701">
                    <Sparkles className="text-indigo-600 w-5 h-5" data-id="element-3702" />
                    Recommendations
                  </h3>
                  <ul className="space-y-4" data-id="element-3703">
                    {getRecommendations(riskLevel).map((rec, i) => <li key={i} className="flex items-start gap-3 text-indigo-900/80" data-id="element-3704">
                        <div className="mt-2 w-1.5 h-1.5 rounded-full bg-indigo-500 flex-shrink-0" data-id="element-3705" />
                        <span className="leading-relaxed" data-id="element-3706">{rec}</span>
                      </li>)}
                  </ul>
                </div>
              </div>}

            {activeTab === 'patterns' && <div className="space-y-4" data-id="element-3707">
                {findings.length > 0 ? <div className="grid grid-cols-1 gap-4" data-id="element-3708">
                    {findings.map((item, idx) => <FindingCard key={`${item.pattern}-${idx}`} pattern={item.pattern} severity={item.severity} example={item.example} index={idx} data-id="element-3709" />)}
                  </div> : patterns.length > 0 ?
            // Fallback to patterns if no findings
            patterns.map((pattern, idx) => <PatternCard key={idx} pattern={pattern} index={idx} data-id="element-3710" />) : <EmptyState data-id="element-3711" />}
              </div>}

            {activeTab === 'timeline' && <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200" data-id="element-3712">
                <h3 className="text-lg font-bold text-slate-900 mb-8" data-id="element-3713">
                  Toxicity Over Time
                </h3>
                <div className="space-y-8" data-id="element-3714">
                  <TimelineItem week="Week 1" score={Math.max(10, score - 25)} data-id="element-3715" />
                  <TimelineItem week="Week 2" score={Math.max(20, score - 15)} data-id="element-3716" />
                  <TimelineItem week="Week 3" score={Math.max(15, score - 5)} data-id="element-3717" />
                  <TimelineItem week="Week 4" score={score} current data-id="element-3718" />
                </div>
              </div>}
          </motion.div>
        </AnimatePresence>

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t border-slate-200 text-center" data-id="element-3719">
          <p className="text-slate-500 text-sm mb-2 font-medium" data-id="element-3720">
            Made with ToxicDetector.ai
          </p>
          <p className="text-slate-400 text-xs max-w-lg mx-auto leading-relaxed font-mono" data-id="element-3721">
            This analysis is based on communication patterns and does not
            diagnose intent or mental health conditions. For professional
            support, please consult a licensed therapist.
          </p>
        </footer>
      </main>

      {/* --- Modals & Overlays --- */}

      {/* Share Modal */}
      <SocialShareModal isOpen={showShareModal} onClose={() => setShowShareModal(false)} shareAspectRatio={shareAspectRatio} onAspectRatioChange={setShareAspectRatio} onSocialShare={handleSocialShare} onDownloadImage={handleDownloadImage} onDownloadPDF={handleDownloadPDF} isGeneratingImage={isGeneratingImage} isGeneratingPDF={isGeneratingPDF} score={score} riskLevel={riskLevel as 'low' | 'medium' | 'high'} messageCount={scan.message_count || 0} patterns={patterns} summary={analysisSummary} shareCardTheme={shareCardTheme} onThemeChange={setShareCardTheme} data-id="element-3722" />

      {/* Delete Confirmation */}
      <AnimatePresence data-id="element-3723">
        {showDeleteConfirm && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-3724">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl" data-id="element-3725">
              <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-3726">
                Delete Scan?
              </h3>
              <p className="text-slate-600 mb-6" data-id="element-3727">
                This action cannot be undone. The analysis will be permanently
                removed.
              </p>
              <div className="flex gap-3" data-id="element-3728">
                <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 py-2.5 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors" data-id="element-3729">
                  Cancel
                </button>
                <button onClick={handleDelete} disabled={deleting} className="flex-1 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-70" data-id="element-3730">
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Hidden Capture Elements */}
      <div className="fixed -left-[9999px] top-0 pointer-events-none" data-id="element-3731">
        <div ref={squareCaptureRef} className={`w-[1080px] h-[1080px] ${shareCardTheme === 'dark' ? 'bg-[#0A0A0A]' : 'bg-[#FAFAFA]'}`} data-id="element-3732">
          <ShareableResultCard score={score} riskLevel={riskLevel} messageCount={scan.message_count || 0} topPatterns={patterns.slice(0, 3)} variant="square" summary={analysisSummary || undefined} theme={shareCardTheme} data-id="element-3733" />
        </div>
        <div ref={storyCaptureRef} className={`w-[1080px] h-[1920px] ${shareCardTheme === 'dark' ? 'bg-[#0A0A0A]' : 'bg-[#FAFAFA]'}`} data-id="element-3734">
          <ShareableResultCard score={score} riskLevel={riskLevel} messageCount={scan.message_count || 0} topPatterns={patterns.slice(0, 4)} variant="vertical" summary={analysisSummary || undefined} theme={shareCardTheme} data-id="element-3735" />
        </div>
      </div>

      <FirstScanReviewModal isOpen={showFirstScanReview} onClose={() => setShowFirstScanReview(false)} data-id="element-3736" />
    </div>;
}
// --- Subcomponents ---
function ScoreRing({
  score,
  riskLevel,
  size = 'normal'
}: {
  score: number;
  riskLevel: string;
  size?: 'normal' | 'large';
}) {
  const radius = size === 'large' ? 80 : 58;
  const strokeWidth = size === 'large' ? 12 : 8;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - score / 100 * circumference;
  const width = size === 'large' ? 200 : 160;
  const getColor = () => {
    if (riskLevel === 'high') return '#ef4444'; // red-500
    if (riskLevel === 'medium') return '#f59e0b'; // amber-500
    return '#10b981'; // emerald-500
  };
  const getTextColor = () => {
    // In dark mode (locked state), we want white text for the score
    // In light mode (unlocked state), we want slate-900
    // We can infer mode from size since large is only used in locked state
    return size === 'large' ? 'text-white' : 'text-slate-900';
  };
  const getBgRingColor = () => {
    return size === 'large' ? 'text-gray-800' : 'text-slate-100';
  };
  return <div className={`relative flex items-center justify-center`} style={{
    width: width,
    height: width
  }} data-id="element-3737">
      <svg className="w-full h-full transform -rotate-90" data-id="element-3738">
        {/* Background Ring */}
        <circle cx={width / 2} cy={width / 2} r={radius} stroke="currentColor" strokeWidth={strokeWidth} fill="transparent" className={getBgRingColor()} data-id="element-3739" />
        {/* Progress Ring */}
        <motion.circle initial={{
        strokeDashoffset: circumference
      }} animate={{
        strokeDashoffset: offset
      }} transition={{
        duration: 1.5,
        ease: 'easeOut'
      }} cx={width / 2} cy={width / 2} r={radius} stroke={getColor()} strokeWidth={strokeWidth} fill="transparent" strokeDasharray={circumference} strokeLinecap="round" data-id="element-3740" />
      </svg>

      <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-3741">
        <motion.span className={`${size === 'large' ? 'text-6xl' : 'text-5xl'} font-extrabold ${getTextColor()} tracking-tight`} initial={{
        opacity: 0,
        scale: 0.5
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} data-id="element-3742">
          {score}
        </motion.span>
        <span className={`text-xs font-bold uppercase tracking-wider mt-1 px-2 py-0.5 rounded-full ${getRiskBadgeColor(riskLevel)}`} data-id="element-3743">
          {riskLevel} Risk
        </span>
      </div>
    </div>;
}
function PatternCard({
  pattern,
  index,
  compact = false
}: {
  pattern: any;
  index: number;
  compact?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  // FIX: Handle both string patterns and object patterns
  const isStringPattern = typeof pattern === 'string';
  const patternType = isStringPattern ? pattern : pattern.type || 'Unknown Pattern';
  const patternSeverity = isStringPattern ? 'MEDIUM' : pattern.severity || 'MEDIUM';
  const patternDescription = isStringPattern ? `This pattern was detected in your conversation.` : pattern.description || 'No description available';
  const patternCount = isStringPattern ? 1 : pattern.count || 1;
  const patternExamples = isStringPattern ? [] : pattern.examples || [];
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: index * 0.1
  }} className={`bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-indigo-200 transition-all hover:shadow-sm ${compact ? 'p-5' : 'p-6'}`} data-id="element-3744">
      <div className="flex items-start gap-4" data-id="element-3745">
        <div className={`p-3 rounded-xl flex-shrink-0 ${getPatternIconBg(patternSeverity)}`} data-id="element-3746">
          {getPatternIcon(patternType)}
        </div>

        <div className="flex-1 min-w-0" data-id="element-3747">
          <div className="flex items-center justify-between mb-2" data-id="element-3748">
            <h4 className="font-bold text-slate-900 truncate pr-2 text-lg tracking-tight" data-id="element-3749">
              {patternType}
            </h4>
            <span className={`text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wide ${getPatternBadgeColor(patternSeverity)}`} data-id="element-3750">
              {patternSeverity}
            </span>
          </div>

          <p className={`text-slate-600 leading-relaxed ${compact ? 'text-sm line-clamp-2' : 'text-base'}`} data-id="element-3751">
            {patternDescription}
          </p>

          {!compact && !isStringPattern && <div className="mt-4" data-id="element-3752">
              <div className="flex items-center justify-between" data-id="element-3753">
                <span className="text-xs font-medium text-slate-400 font-mono" data-id="element-3754">
                  {patternCount} instances detected
                </span>
                {patternExamples.length > 0 && <button onClick={() => setExpanded(!expanded)} className="text-sm font-medium text-indigo-600 hover:text-indigo-700 flex items-center gap-1 transition-colors" data-id="element-3755">
                    {expanded ? 'Hide Examples' : 'Show Examples'}
                    {expanded ? <ChevronUp size={16} data-id="element-3756" /> : <ChevronDown size={16} data-id="element-3757" />}
                  </button>}
              </div>

              <AnimatePresence data-id="element-3758">
                {expanded && patternExamples.length > 0 && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} className="overflow-hidden" data-id="element-3759">
                    <div className="pt-4 space-y-2" data-id="element-3760">
                      {patternExamples.map((ex: string, i: number) => <div key={i} className="bg-slate-50 p-3 rounded-lg text-sm text-slate-700 border border-slate-100 italic" data-id="element-3761">
                          "{ex}"
                        </div>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>}
        </div>
      </div>
    </motion.div>;
}
function TimelineItem({
  week,
  score,
  current
}: {
  week: string;
  score: number;
  current?: boolean;
}) {
  return <div className="flex items-center gap-4" data-id="element-3762">
      <div className="w-16 text-sm font-medium text-slate-500" data-id="element-3763">{week}</div>
      <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden" data-id="element-3764">
        <motion.div initial={{
        width: 0
      }} animate={{
        width: `${score}%`
      }} transition={{
        duration: 1,
        ease: 'easeOut'
      }} className={`h-full rounded-full ${current ? 'bg-indigo-600' : 'bg-slate-300'}`} data-id="element-3765" />
      </div>
      <div className="w-12 text-right font-bold text-slate-700 tabular-nums" data-id="element-3766">
        {score}%
      </div>
    </div>;
}
function TabButton({
  active,
  children,
  onClick
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return <button onClick={onClick} className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all ${active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'}`} data-id="element-3767">
      {children}
    </button>;
}
function EmptyState() {
  return <div className="text-center py-12 bg-white rounded-2xl border border-slate-200 border-dashed" data-id="element-3768">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-50 text-green-600 mb-4" data-id="element-3769">
        <Check size={32} data-id="element-3770" />
      </div>
      <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-3771">
        No Patterns Detected
      </h3>
      <p className="text-slate-500" data-id="element-3772">This conversation appears to be healthy.</p>
    </div>;
}
// --- Loading / Error States ---
function LoadingState() {
  return <div className="flex flex-col w-full min-h-screen bg-slate-50" data-id="element-3773">
      <WebsiteHeader data-id="element-3774" />
      <div className="flex-1 flex items-center justify-center" data-id="element-3775">
        <LoadingAnimation data-id="element-3776" />
      </div>
    </div>;
}
function ProcessingState({
  onBack
}: {
  onBack: () => void;
}) {
  return <div className="flex flex-col w-full min-h-screen bg-slate-50" data-id="element-3777">
      <WebsiteHeader data-id="element-3778" />
      <div className="flex-1 flex flex-col items-center justify-center p-6 text-center" data-id="element-3779">
        <LoadingAnimation status="processing" data-id="element-3780" />
        <p className="mt-8 text-slate-600 font-medium animate-pulse" data-id="element-3781">
          Analyzing conversation patterns...
        </p>
        <button onClick={onBack} className="mt-4 text-indigo-600 font-semibold hover:underline" data-id="element-3782">
          Back to Dashboard
        </button>
      </div>
    </div>;
}
function ErrorState({
  message,
  onRetry,
  onBack
}: {
  message: string;
  onRetry?: () => void;
  onBack: () => void;
}) {
  return <div className="flex flex-col w-full min-h-screen bg-slate-50" data-id="element-3783">
      <WebsiteHeader data-id="element-3784" />
      <div className="flex-1 flex flex-col items-center justify-center p-6 text-center" data-id="element-3785">
        <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mb-4" data-id="element-3786">
          <AlertTriangle size={32} data-id="element-3787" />
        </div>
        <h2 className="text-xl font-bold text-slate-900 mb-2" data-id="element-3788">
          Something went wrong
        </h2>
        <p className="text-slate-500 mb-6 max-w-md" data-id="element-3789">{message}</p>
        <div className="flex gap-3" data-id="element-3790">
          {onRetry && <button onClick={onRetry} className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700" data-id="element-3791">
              Retry
            </button>}
          <button onClick={onBack} className="px-6 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-50" data-id="element-3792">
            Dashboard
          </button>
        </div>
      </div>
    </div>;
}
function FailedState({
  onRetry,
  onBack,
  retrying
}: {
  onRetry: () => void;
  onBack: () => void;
  retrying: boolean;
}) {
  return <div className="flex flex-col w-full min-h-screen bg-slate-50" data-id="element-3793">
      <WebsiteHeader data-id="element-3794" />
      <div className="flex-1 flex flex-col items-center justify-center p-6" data-id="element-3795">
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200 max-w-md w-full text-center" data-id="element-3796">
          <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-3797">
            <X size={32} data-id="element-3798" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2" data-id="element-3799">
            Analysis Failed
          </h2>
          <p className="text-slate-500 mb-6" data-id="element-3800">
            We encountered an issue processing your conversation.
          </p>
          <button onClick={onRetry} disabled={retrying} className="w-full py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 disabled:opacity-70 mb-3 flex items-center justify-center gap-2" data-id="element-3801">
            {retrying && <RefreshCw size={18} className="animate-spin" data-id="element-3802" />}
            {retrying ? 'Retrying...' : 'Retry Analysis'}
          </button>
          <button onClick={onBack} className="text-slate-500 font-medium hover:text-slate-800" data-id="element-3803">
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>;
}
// --- Helpers ---
function getRiskColorBar(level: string) {
  if (level === 'high') return 'bg-red-500';
  if (level === 'medium') return 'bg-amber-500';
  return 'bg-emerald-500';
}
function getRiskBadgeColor(level: string) {
  if (level === 'high') return 'bg-red-100 text-red-700';
  if (level === 'medium') return 'bg-amber-100 text-amber-700';
  return 'bg-emerald-100 text-emerald-700';
}
// FIX: Update headline copy to match toxicity semantics (higher score = more toxic = high risk)
function getRiskHeadline(level: string) {
  if (level === 'high') return 'High Risk Communication Pattern Detected';
  if (level === 'medium') return 'Potentially Concerning Communication';
  return 'No Major Toxic Patterns Detected';
}
function getRiskDescription(level: string, count: number) {
  if (level === 'high') return `We identified ${count} distinct patterns that may indicate unhealthy dynamics. It's important to review these carefully.`;
  if (level === 'medium') return `We found ${count} areas where communication could be improved. These patterns are common but worth addressing.`;
  return `Great news! We found ${count > 0 ? count + ' minor items' : 'no concerning patterns'}, and overall the communication style appears respectful and safe.`;
}
function getPatternBadgeColor(severity: string) {
  if (severity === 'HIGH') return 'bg-red-100 text-red-700';
  if (severity === 'MEDIUM') return 'bg-amber-100 text-amber-700';
  return 'bg-emerald-100 text-emerald-700';
}
function getPatternIconBg(severity: string) {
  if (severity === 'HIGH') return 'bg-red-50 text-red-600';
  if (severity === 'MEDIUM') return 'bg-amber-50 text-amber-600';
  return 'bg-emerald-50 text-emerald-600';
}
function getPatternIcon(type: string) {
  const map: Record<string, React.ReactNode> = {
    Gaslighting: <AlertTriangle size={20} data-id="element-3804" />,
    Manipulation: <Shield size={20} data-id="element-3805" />,
    Dismissiveness: <MessageSquare size={20} data-id="element-3806" />,
    'Blame Shifting': <TrendingDown size={20} data-id="element-3807" />,
    'Emotional Abuse': <AlertTriangle size={20} data-id="element-3808" />
  };
  return map[type] || <AlertTriangle size={20} data-id="element-3809" />;
}
function getRecommendations(level: string) {
  if (level === 'high') return ['Document these conversations for your own clarity.', 'Consider setting firm boundaries around acceptable language.', 'Trust your instincts if you feel unsafe or confused.', 'Seek support from a professional to navigate these dynamics.'];
  if (level === 'medium') return ['Try to address these patterns when you are both calm.', 'Use "I" statements to express how these messages make you feel.', 'Monitor if these patterns become more frequent over time.', 'Focus on clear, direct communication to avoid misunderstandings.'];
  return ['Continue fostering open and respectful dialogue.', 'Celebrate the positive aspects of your communication.', 'Address small misunderstandings before they grow.', 'Keep checking in with each other regularly.'];
}